/**
 * IndexedDB-backed draft store for the upload screens.
 *
 * The problem it solves: files and all their metadata (labels, buildings, units,
 * dates) lived only in component state. Navigating to another part of the app, or
 * anything else that unmounted the upload screen, silently threw the whole batch
 * away - which is what users experience as "it resets when I switch tabs".
 *
 * IndexedDB is used rather than sessionStorage because `File`/`Blob` values
 * survive IndexedDB's structured clone but cannot be stored as strings without
 * base64-inflating every byte of every file.
 */

import * as Sentry from "@sentry/react";

const DB_NAME = "yallyhub-uploads";
const DB_VERSION = 1;
const STORE_NAME = "drafts";

/** Drafts older than this are dropped instead of restored. */
const MAX_DRAFT_AGE_MS = 24 * 60 * 60 * 1000;

export interface UploadDraft<TMeta = Record<string, unknown>> {
  /** Distinguishes drafts per screen/context, e.g. "upload-files:pictures". */
  key: string;
  files: File[];
  /** Whatever the screen needs to rebuild its rows; stored as-is. */
  meta: TMeta;
  savedAt: number;
}

/**
 * Files and metadata are stored under separate keys.
 *
 * They were one record, which meant every debounced save rewrote every file blob.
 * Editing a label on a batch of 39 five-megabyte photos wrote ~195MB to disk per
 * keystroke-plus-debounce. Metadata changes constantly and is tiny; the blobs are
 * enormous and change only when a file is added or removed.
 */
const filesKey = (key: string) => `${key}::files`;
const metaKey = (key: string) => `${key}::meta`;

interface FilesRecord {
  key: string;
  files: File[];
  savedAt: number;
}

interface MetaRecord<TMeta> {
  key: string;
  meta: TMeta;
  savedAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

const openDatabase = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is niet beschikbaar in deze browser"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Kon IndexedDB niet openen"));
  });

  // A failed open must not poison every later call.
  dbPromise.catch(() => {
    dbPromise = null;
  });

  return dbPromise;
};

const runTransaction = async <T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const request = work(transaction.objectStore(STORE_NAME));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB-bewerking mislukt"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("IndexedDB-transactie afgebroken"));
  });
};

/**
 * Writes only the metadata record. Cheap enough to run on every debounce.
 */
export const saveUploadDraftMeta = async <TMeta>(
  key: string,
  meta: TMeta,
): Promise<void> => {
  try {
    const record: MetaRecord<TMeta> = {
      key: metaKey(key),
      meta,
      savedAt: Date.now(),
    };
    await runTransaction("readwrite", (store) => store.put(record));
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      extra: { context: "uploadDraftStore.saveUploadDraftMeta", key },
    });
  }
};

/**
 * Writes the file blobs. Callers should only do this when the file list actually
 * changed - this is the expensive half by orders of magnitude.
 */
export const saveUploadDraftFiles = async (
  key: string,
  files: File[],
): Promise<void> => {
  try {
    const record: FilesRecord = {
      key: filesKey(key),
      files,
      savedAt: Date.now(),
    };
    await runTransaction("readwrite", (store) => store.put(record));
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      extra: {
        context: "uploadDraftStore.saveUploadDraftFiles",
        key,
        fileCount: files.length,
        totalBytes: files.reduce((sum, file) => sum + file.size, 0),
      },
    });
  }
};

/**
 * Persists both halves. Kept for callers that genuinely change both at once.
 */
export const saveUploadDraft = async <TMeta>(
  key: string,
  files: File[],
  meta: TMeta,
): Promise<void> => {
  await Promise.all([
    saveUploadDraftFiles(key, files),
    saveUploadDraftMeta(key, meta),
  ]);
};

/** Returns the stored draft, or null when absent, stale or unreadable. */
export const loadUploadDraft = async <TMeta>(
  key: string,
): Promise<UploadDraft<TMeta> | null> => {
  try {
    const [filesRecord, metaRecord] = await Promise.all([
      runTransaction<FilesRecord | undefined>("readonly", (store) =>
        store.get(filesKey(key)),
      ),
      runTransaction<MetaRecord<TMeta> | undefined>("readonly", (store) =>
        store.get(metaKey(key)),
      ),
    ]);

    // A draft with no files is not worth restoring and only creates confusion.
    if (!filesRecord || !Array.isArray(filesRecord.files) || filesRecord.files.length === 0) {
      await clearUploadDraft(key);
      return null;
    }

    if (Date.now() - filesRecord.savedAt > MAX_DRAFT_AGE_MS) {
      await clearUploadDraft(key);
      return null;
    }

    return {
      key,
      files: filesRecord.files,
      meta: (metaRecord?.meta ?? {}) as TMeta,
      savedAt: filesRecord.savedAt,
    };
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      extra: { context: "uploadDraftStore.loadUploadDraft", key },
    });
    return null;
  }
};

export const clearUploadDraft = async (key: string): Promise<void> => {
  try {
    await Promise.all([
      runTransaction("readwrite", (store) => store.delete(filesKey(key))),
      runTransaction("readwrite", (store) => store.delete(metaKey(key))),
      // Drafts written before files and metadata were split lived under the bare
      // key; drop those too so they cannot linger forever.
      runTransaction("readwrite", (store) => store.delete(key)),
    ]);
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      extra: { context: "uploadDraftStore.clearUploadDraft", key },
    });
  }
};

/** Test seam: forces the next call to reopen the database. */
export const resetUploadDraftStoreForTests = () => {
  dbPromise = null;
};
